import {
  CLAIM_TABLE_SURFACE_COLOR,
  claimTableStyles,
} from 'styles/ClaimTableStyles';
import { ClaimTableToolbar } from './ClaimTableToolbar';
import {
  Dispatch,
  MouseEvent,
  MouseEventHandler,
  MutableRefObject,
  SetStateAction,
} from 'react';

import {
  MRT_TopToolbar,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_ExpandedState,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_VisibilityState,
} from 'mantine-react-table';

import { CountReturnTypes } from 'api/utils/queries';
import { QueryClient } from '@tanstack/react-query';
import { Row } from '@tanstack/react-table';
import { NavigateFunction } from 'react-router-dom';
import { Flex, Stack, ActionIcon, alpha, MantineTheme } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';

import { CLAIM_TARIFF_STATUS, ClaimConfidenceInfo } from '@billing/validation';
import { TableAlertBanner, TableProgressBar } from 'components/common';
import {
  ClaimCreateRowModalContent,
  ClaimEditRowModalContent,
  BulkClaimEditPayload,
  ClaimBulkEditModal,
  StatusFilterButtons,
  providerInitialVisibleColumns,
} from 'pages/claims/components';

import {
  Claim,
  CreateClaimForm,
  Payor,
  Provider,
  SharedClaimForm,
  Site,
  Status,
} from 'interfaces';
import {
  ClaimTableRow,
  getParentClaim,
  isClaimParentRow,
  isClaimTariffRow,
} from 'pages/claims/utils/claimTableRows';
import { claimHasTariffStatus } from 'utils/claimStatusUtils';
import { toggleClaimRowFromClick } from 'pages/claims/utils/claimRowExpansion';
import { CLAIM_ACTION_COLUMN_ID } from './ClaimConstants';
import { shouldDefaultBulkEditServiceTimes } from 'utils/dateUtils';
import { cloneColumnFilters, areColumnFiltersEqual } from 'utils/filterUtils';
import {
  ProviderClaimSelectors,
  ProviderNeedsYou,
} from './ProviderClaimFilters';

const pinnedClaimColumnIDs = [
  'mrt-row-expand',
  'mrt-row-select',
  CLAIM_ACTION_COLUMN_ID,
] as const;
const pinnedClaimColumnIDSet = new Set<string>(pinnedClaimColumnIDs);

const getClaimColumnOrder = (columnOrder: string[]): string[] => [
  ...pinnedClaimColumnIDs,
  ...columnOrder.filter((ID) => !pinnedClaimColumnIDSet.has(ID)),
];

type ClaimPaginationState = {
  pageIndex: number;
  pageSize: number;
  globalFilter: string | undefined;
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
};

type ClaimTableOptionsProps = {
  tableRows: ClaimTableRow[];
  columns: MRT_ColumnDef<ClaimTableRow>[];
  pagination: ClaimPaginationState;
  setPagination: Dispatch<SetStateAction<ClaimPaginationState>>;
  visibleColumns: MRT_VisibilityState;
  setVisibleColumns: Dispatch<SetStateAction<MRT_VisibilityState>>;
  initialColumns: MRT_VisibilityState;
  expanded: MRT_ExpandedState;
  setExpanded: Dispatch<SetStateAction<MRT_ExpandedState>>;
  rowSelection: MRT_RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<MRT_RowSelectionState>>;
  columnOrder: string[];
  setColumnOrder: Dispatch<SetStateAction<string[]>>;
  persistedColumnSizing: Record<string, number>;
  isFetching: boolean;
  isLoading: boolean;
  queryPending: boolean;
  manualLoading: boolean;
  isError: boolean;
  errorMessage: string;
  isProvider: boolean;
  isAdmin: boolean;
  mode: 'light' | 'dark';
  theme: MantineTheme;
  providers: Provider[];
  payors: Payor[];
  payorTariffCounts?: Record<string, number>;
  site: Site;
  status: Status[];
  claimStatuses: CountReturnTypes;
  providerActionRequestCount: number;
  actionNeededCount?: number;
  rowCount: number;
  queryClient?: QueryClient;
  navigate: NavigateFunction;
  handleUpdateClaim: (props: {
    values: Claim;
    row: MRT_Row<Claim>;
    table: MRT_TableInstance<Claim>;
  }) => Promise<void>;
  handleCreatingRowSave: (props: {
    exitCreatingMode: () => void;
    row: MRT_Row<ClaimTableRow>;
    table: MRT_TableInstance<ClaimTableRow>;
    values: Record<string, unknown>;
  }) => Promise<void>;
  resetDirtyFields: () => void;
  dirtyFields: MutableRefObject<Record<string, boolean>>;
  confidenceMetadataByClaimID: Record<string, ClaimConfidenceInfo>;
  stagedTariffActionsRef: MutableRefObject<{
    claimID: string | null;
    hasStagedActions: boolean;
  }>;
  clearStagedTariffActions: () => void;
  selectClaimRows: (claimID: string) => void;
  setSelectedClaim: (claim: Claim | null) => void;
  setShowPreview: (show: boolean) => void;
  setViewActionsOpen: Dispatch<SetStateAction<boolean>>;
  useSelectedClaimForDuplicate: boolean;
  setUseSelectedClaimForDuplicate: Dispatch<SetStateAction<boolean>>;
  quickCreatePrefill: {
    createInitialValues?: Partial<CreateClaimForm>;
    bulkSharedInitialValues?: Partial<SharedClaimForm>;
  } | null;
  setQuickCreatePrefill: Dispatch<
    SetStateAction<{
      createInitialValues?: Partial<CreateClaimForm>;
      bulkSharedInitialValues?: Partial<SharedClaimForm>;
    } | null>
  >;
  createModalType: 'claim' | 'bulk' | 'repeat';
  closeEditModalFromShell: () => void;
  bulkEditOpen: boolean;
  bulkEditClaimIDs: string[];
  closeBulkEditModal: () => void;
  handleConfirmBulkEdit: (
    payload: BulkClaimEditPayload
  ) => Promise<void> | void;
  handleTableContextMenu: MouseEventHandler<HTMLTableElement>;
  setPersistedVisibleColumns: (columns: MRT_VisibilityState) => void;
  setPersistedColumnOrder: (columnOrder: string[]) => void;
  setPersistedColumnSizing: (
    sizing:
      | Record<string, number>
      | ((current: Record<string, number>) => Record<string, number>)
  ) => void;
  mantineTableBodyRowProps: (props: {
    row: MRT_Row<Claim>;
  }) => Record<string, unknown>;
};

const canExpandClaimRow = (row: Row<ClaimTableRow>): boolean =>
  isClaimParentRow(row.original) && (row.original.subRows?.length ?? 0) >= 1;

const CLAIM_EDIT_MODAL_HEADER_CLASS_NAME = 'claim-edit-modal-header';

export const getClaimTableOptions = ({
  tableRows,
  columns,
  pagination,
  setPagination,
  visibleColumns,
  setVisibleColumns,
  initialColumns,
  expanded,
  setExpanded,
  rowSelection,
  setRowSelection,
  columnOrder,
  setColumnOrder,
  persistedColumnSizing,
  isFetching,
  isLoading,
  queryPending,
  manualLoading,
  isError,
  errorMessage,
  isProvider,
  isAdmin,
  mode,
  theme,
  providers,
  payors,
  payorTariffCounts = {},
  site,
  status,
  claimStatuses,
  providerActionRequestCount,
  actionNeededCount,
  rowCount,
  queryClient,
  navigate,
  handleUpdateClaim,
  handleCreatingRowSave,
  resetDirtyFields,
  dirtyFields,
  confidenceMetadataByClaimID,
  stagedTariffActionsRef,
  clearStagedTariffActions,
  selectClaimRows,
  setSelectedClaim,
  setShowPreview,
  setViewActionsOpen,
  useSelectedClaimForDuplicate,
  setUseSelectedClaimForDuplicate,
  quickCreatePrefill,
  setQuickCreatePrefill,
  createModalType,
  closeEditModalFromShell,
  bulkEditOpen,
  bulkEditClaimIDs,
  closeBulkEditModal,
  handleConfirmBulkEdit,
  handleTableContextMenu,
  setPersistedVisibleColumns,
  setPersistedColumnOrder,
  setPersistedColumnSizing,
  mantineTableBodyRowProps,
}: ClaimTableOptionsProps): MRT_TableOptions<ClaimTableRow> => ({
  data: tableRows,
  columns,
  displayColumnDefOptions: {
    'mrt-row-expand': {
      header: '',
      size: 32,
      minSize: 32,
      maxSize: 32,
      grow: false,
      mantineTableHeadCellProps: { style: { paddingInline: 4 } },
      mantineTableBodyCellProps: { style: { paddingInline: 4 } },
      Header: ({ table }) => {
        const canExpandRows = table.getCanSomeRowsExpand();
        const isAllRowsExpanded = table.getIsAllRowsExpanded();

        return (
          <Flex direction="column" align="flex-start" gap={4} w="100%">
            <ActionIcon
              size="xs"
              radius="xl"
              variant="subtle"
              className="claims-expand-button"
              onClick={(event) => {
                event.stopPropagation();
                table.toggleAllRowsExpanded(!isAllRowsExpanded);
              }}
              disabled={!canExpandRows}
              aria-label={
                isAllRowsExpanded ? 'Collapse all claims' : 'Expand all claims'
              }
              data-testid="claims-toggle-expand-all"
            >
              <IconChevronDown
                size={16}
                stroke={1.8}
                style={{
                  transform: isAllRowsExpanded ? 'rotate(180deg)' : undefined,
                  transition: 'transform 120ms ease',
                }}
              />
            </ActionIcon>
          </Flex>
        );
      },
    },
    'mrt-row-select': {
      size: 28,
      minSize: 28,
      maxSize: 28,
      grow: false,
      mantineTableHeadCellProps: { style: { paddingInline: 4 } },
      mantineTableBodyCellProps: { style: { paddingInline: 4 } },
    },
  },
  enableStickyHeader: true,
  initialState: {
    globalFilter: pagination.globalFilter || '',
    density: 'md',
    columnPinning: {
      left: [...pinnedClaimColumnIDs],
    },
    columnOrder: getClaimColumnOrder(
      !isProvider ? columnOrder : providerInitialVisibleColumns
    ),
    columnFilters: pagination.columnFilters || [],
    expanded: {},
    showColumnFilters: true,
    showGlobalFilter: !!pagination.globalFilter,
    columnVisibility: initialColumns,
  },
  state: {
    pagination,
    columnVisibility: visibleColumns,
    showProgressBars: isFetching || isLoading || queryPending,
    globalFilter: pagination.globalFilter || '',
    columnFilters: pagination.columnFilters || [],
    showSkeletons: isFetching || isLoading,
    showLoadingOverlay:
      queryPending || isFetching || isLoading || manualLoading,
    showAlertBanner: isError,
    sorting: pagination.sorting || [],
    expanded,
    columnOrder: getClaimColumnOrder(columnOrder),
    columnSizing: persistedColumnSizing,
    rowSelection: isProvider || isAdmin ? {} : rowSelection,
  },
  getRowId: (row) => row.rowId || row.id,
  getSubRows: (row) => row.subRows,
  enableFilterMatchHighlighting: !(isProvider || isAdmin),
  enableRowPinning: false,
  createDisplayMode: 'modal',
  editDisplayMode: 'modal',
  enableMultiRowSelection: true,
  // Keep tariff selection independent so selecting a subrow does not auto-select the parent claim.
  enableSubRowSelection: true,
  enableBatchRowSelection: false,
  positionToolbarAlertBanner: 'none',
  enableSelectAll: true,
  enableRowSelection: !(isProvider || isAdmin),
  enableRowActions: false,
  enableEditing: false,
  sortDescFirst: true,
  isMultiSortEvent: () => true,
  manualFiltering: true,
  enableExpanding: true,
  enableExpandAll: false,
  enableColumnPinning: true,
  enableColumnDragging: true,
  enableColumnOrdering: true,
  enableColumnResizing: true,
  columnResizeMode: 'onEnd',
  enableHiding: true,
  enableGlobalFilter: true,
  positionGlobalFilter: 'none',
  enableToolbarInternalActions: false,
  getRowCanExpand: canExpandClaimRow,
  onExpandedChange: setExpanded,
  onSortingChange: (updater) =>
    setPagination((prev) => {
      const newSorting =
        typeof updater === 'function'
          ? (updater as (old: MRT_SortingState) => MRT_SortingState)(
              cloneSorting(prev.sorting)
            )
          : (updater as MRT_SortingState);

      if (areSortingEqual(prev.sorting, newSorting)) {
        return prev;
      }

      return {
        ...prev,
        sorting: newSorting,
        pageIndex: 0,
      };
    }),
  enableFullScreenToggle: false,
  onColumnFiltersChange: (updater) =>
    setPagination((prev) => {
      const newFilters =
        typeof updater === 'function'
          ? (
              updater as (old: MRT_ColumnFiltersState) => MRT_ColumnFiltersState
            )(cloneColumnFilters(prev.columnFilters))
          : (updater as MRT_ColumnFiltersState);

      if (areColumnFiltersEqual(prev.columnFilters, newFilters)) {
        return prev;
      }

      return {
        ...prev,
        columnFilters: newFilters,
        pageIndex: 0,
      };
    }),
  onGlobalFilterChange: (updater) =>
    setPagination((prev) => {
      const newGlobalFilter =
        typeof updater === 'function'
          ? (updater as (old: string | undefined) => string | undefined)(
              prev.globalFilter
            )
          : (updater as string | undefined);

      if (areGlobalFiltersEqual(prev.globalFilter, newGlobalFilter)) {
        return prev;
      }

      return {
        ...prev,
        globalFilter: newGlobalFilter ?? '',
        pageIndex: 0,
      };
    }),
  onColumnVisibilityChange: (updater) => {
    setVisibleColumns((prev) => {
      const next =
        typeof updater === 'function'
          ? (updater as (old: typeof prev) => typeof prev)(prev)
          : (updater as typeof prev);
      setPersistedVisibleColumns(next);
      return next;
    });
  },
  onPaginationChange: (updater) =>
    setPagination((prev) => {
      const nextPagination =
        typeof updater === 'function'
          ? updater({
              pageIndex: prev.pageIndex,
              pageSize: prev.pageSize,
            })
          : updater;

      if (
        prev.pageIndex === nextPagination.pageIndex &&
        prev.pageSize === nextPagination.pageSize
      ) {
        return prev;
      }

      return {
        ...prev,
        pageIndex: nextPagination.pageIndex,
        pageSize: nextPagination.pageSize,
      };
    }),
  onColumnOrderChange: (updater) => {
    setColumnOrder((prev) => {
      const next =
        typeof updater === 'function'
          ? (updater as (old: string[]) => string[])(getClaimColumnOrder(prev))
          : (updater as string[]);

      const dataColumnOrder = next.filter(
        (ID) => !pinnedClaimColumnIDSet.has(ID)
      );
      setPersistedColumnOrder(dataColumnOrder);
      return dataColumnOrder;
    });
  },
  onColumnSizingChange: setPersistedColumnSizing,
  manualPagination: true,
  rowCount,
  onEditingRowSave: async ({ values, row, table }) => {
    await handleUpdateClaim({
      values: values as Claim,
      row: row as unknown as MRT_Row<Claim>,
      table: table as unknown as MRT_TableInstance<Claim>,
    });
    clearStagedTariffActions();
  },
  onCreatingRowSave: handleCreatingRowSave,
  onEditingRowCancel: ({ table }) => {
    resetDirtyFields();
    table.resetRowSelection();
  },
  onCreatingRowCancel: ({ table }) => {
    resetDirtyFields();
    setQuickCreatePrefill(null);
    setUseSelectedClaimForDuplicate(false);
    table.setCreatingRow(null);
  },
  mantineSearchTextInputProps: {
    placeholder: 'Search Claims',
  },
  onRowSelectionChange: setRowSelection,
  mantinePaperProps: {
    withBorder: true,
    radius: 'lg',
    shadow: 'none',
    style: {
      overflow: 'hidden',
      background: CLAIM_TABLE_SURFACE_COLOR,
      '--mrt-base-background-color': CLAIM_TABLE_SURFACE_COLOR,
      '--mrt-table-body-background-color': CLAIM_TABLE_SURFACE_COLOR,
    },
  },
  renderTopToolbar: ({ table }) => (
    <Stack gap={0} pos="relative">
      <StatusFilterButtons
        statusCounts={claimStatuses}
        actionNeededCount={actionNeededCount}
        isProvider={isProvider}
      />
      <MRT_TopToolbar table={table} />
    </Stack>
  ),
  renderTopToolbarCustomActions: ({ table }) => (
    <ClaimTableToolbar
      table={table as unknown as MRT_TableInstance<Claim>}
      queryClient={queryClient}
      isProvider={isProvider}
    >
      {isProvider && (
        <>
          <ProviderNeedsYou
            count={providerActionRequestCount}
            variant="filter"
          />
          <ProviderClaimSelectors
            payors={payors}
            payorTariffCounts={payorTariffCounts}
          />
        </>
      )}
    </ClaimTableToolbar>
  ),
  renderCreateRowModalContent: ({ table, row, internalEditComponents }) => {
    if (payors.length === 0 || !site.id) return null;
    return (
      <ClaimCreateRowModalContent
        table={table as unknown as MRT_TableInstance<Claim>}
        row={row as unknown as MRT_Row<Claim>}
        internalEditComponents={internalEditComponents}
        status={status}
        providers={providers}
        payors={payors}
        site={site}
        createModalType={createModalType}
        useSelectedClaimAsInitialValues={useSelectedClaimForDuplicate}
        quickCreatePrefill={quickCreatePrefill ?? undefined}
      />
    );
  },
  renderEditRowModalContent: ({ table, row, internalEditComponents }) => {
    if (bulkEditOpen) {
      const claimsByID = new Map(
        tableRows.filter(isClaimParentRow).map((claim) => [claim.id, claim])
      );
      const selectedServiceStarts = bulkEditClaimIDs.map(
        (claimID) => claimsByID.get(claimID)?.serviceStart
      );
      const selectedClaims = bulkEditClaimIDs.flatMap((claimID) => {
        const claim = claimsByID.get(claimID);
        return claim ? [claim] : [];
      });

      return (
        <ClaimBulkEditModal
          key={bulkEditClaimIDs.join(':')}
          claimIDs={bulkEditClaimIDs}
          claims={selectedClaims}
          shouldDefaultServiceTimes={shouldDefaultBulkEditServiceTimes(
            selectedServiceStarts,
            site.timezone
          )}
          siteTimezone={site.timezone}
          siteID={site.id}
          row={row}
          internalEditComponents={internalEditComponents}
          providers={providers}
          onClose={closeBulkEditModal}
          onConfirm={handleConfirmBulkEdit}
        />
      );
    }

    return (
      <ClaimEditRowModalContent
        table={table as unknown as MRT_TableInstance<Claim>}
        row={row as unknown as MRT_Row<Claim>}
        internalEditComponents={internalEditComponents}
        providers={providers}
        payors={payors}
        dirtyFields={dirtyFields}
        confidenceMetadata={
          confidenceMetadataByClaimID[row.original.id] ?? null
        }
        stagedTariffActionsRef={stagedTariffActionsRef}
        headerActionsTarget={`.${CLAIM_EDIT_MODAL_HEADER_CLASS_NAME}`}
      />
    );
  },
  mantineToolbarAlertBannerProps: () => {
    return TableAlertBanner({ isError, message: errorMessage });
  },
  mantineProgressProps({ isTopToolbar }) {
    return TableProgressBar({ isTopToolbar });
  },
  mantineTableBodyRowProps: (props) => {
    const { row, table } = props as {
      row: MRT_Row<ClaimTableRow>;
      table: MRT_TableInstance<ClaimTableRow>;
      isDetailPanel?: boolean;
    };
    const claim = getParentClaim(row.original);
    const isSubmitted = claimHasTariffStatus(
      claim,
      CLAIM_TARIFF_STATUS.SUBMITTED
    );
    const invalidHighlightProps = mantineTableBodyRowProps({
      row: row as unknown as MRT_Row<Claim>,
    });

    return {
      ...invalidHighlightProps,
      onClick: (event) => {
        toggleClaimRowFromClick(event, row);
        if (isProvider || isAdmin) return;
        setSelectedClaim(claim);
        setShowPreview(true);
      },
      onDoubleClick: () => {
        if (isProvider) {
          if ((claim.actionRequests?.length ?? 0) === 0) return;
          setSelectedClaim(claim);
          setViewActionsOpen(true);
          return;
        }
        if (isAdmin) return;
        selectClaimRows(claim.id);
        setSelectedClaim(claim);
        const parentRow = table
          .getRowModel()
          .flatRows.find(
            (candidate) =>
              isClaimParentRow(candidate.original) &&
              candidate.original.id === claim.id
          );
        table.setEditingRow(parentRow ?? row);
        navigate(`/claims/edit/${claim.id}`);
      },
      role: 'row',
      'data-claim-id': claim.id,
      ...(isClaimParentRow(row.original) && row.getIsExpanded()
        ? { 'data-testid': `claim-detail-${claim.id}` }
        : {}),
      ...(isClaimTariffRow(row.original) && row.original.tariffId
        ? {
            'data-testid': `claim-detail-tariff-${row.original.tariffId}`,
          }
        : {}),
      ...getClaimRowVisualState({
        row,
        mode,
        theme,
        isSubmitted,
        isProvider,
        isAdmin,
      }),
    };
  },
  mantineTableBodyCellProps: ({ cell, row }) =>
    getClaimCellVisualState({
      cellId: cell.column.id,
      row,
      mode,
      theme,
    }),
  mantineSelectCheckboxProps: ({ row }) => ({
    onClick: (event) => {
      event.stopPropagation();
      setSelectedClaim(getParentClaim(row.original));
      setShowPreview(true);
    },
    onMouseDown: (event) => {
      event.stopPropagation();
    },
    style: {
      cursor: 'pointer',
    },
    styles: {
      root: { cursor: 'pointer' },
      body: { cursor: 'pointer' },
      inner: { cursor: 'pointer' },
      input: { cursor: 'pointer' },
    },
    radius: 'sm',
    size: 'xs',
  }),
  mantineExpandButtonProps: ({ row }) => {
    const canExpand = canExpandClaimRow(row as unknown as Row<ClaimTableRow>);

    return {
      size: 'xs',
      radius: 'xl',
      variant: 'subtle',
      disabled: !canExpand,
      'aria-label': row.getIsExpanded() ? 'Collapse claim' : 'Expand claim',
      className: 'claims-expand-button',
    };
  },
  mantineEditRowModalProps: {
    withCloseButton: false,
    title: bulkEditOpen ? 'Bulk edit claims' : 'Edit Claim',
    classNames: { header: CLAIM_EDIT_MODAL_HEADER_CLASS_NAME },
    size: '100%',
    radius: 'lg',
    centered: true,
    withOverlay: true,
    closeOnClickOutside: false,
    closeOnEscape: false,
    trapFocus: true,
    returnFocus: true,
    keepMounted: true,
    onClose: closeEditModalFromShell,
    overlayProps: {
      backgroundOpacity: 0.5,
      blur: 5,
      onClick: closeEditModalFromShell,
    },
    styles: {
      close: {
        border: `1px solid ${
          mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
        }`,
      },
      header: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
        alignItems: 'center',
      },
      title: {
        fontSize: '1.875rem',
        gridColumn: 2,
        textAlign: 'center',
      },
    },
  },
  mantineCreateRowModalProps: {
    withCloseButton: true,
    closeOnClickOutside: false,
    closeOnEscape: false,
    title:
      createModalType === 'claim'
        ? 'Create Claim'
        : createModalType === 'repeat'
        ? 'Repeat Claim Dates'
        : 'Create Claims',
    size: '100%',
    centered: true,
    withOverlay: true,
    trapFocus: true,
    returnFocus: true,
    keepMounted: true,
    overlayProps: {
      backgroundOpacity: 0.5,
      blur: 5,
    },
    styles: {
      header: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',
      },
      title: {
        fontSize: '1.875rem',
        textAlign: 'center',
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
      },
    },
  },
  mantineTableProps: {
    className: 'claims-table',
    onContextMenu: handleTableContextMenu,
  },
  mantineTopToolbarProps: {
    className: 'claims-top-toolbar',
    style: {
      borderBottom: `1px solid ${
        mode === 'dark' ? theme.colors.gray[8] : theme.colors.gray[3]
      }`,
    },
  },
  mantineTableHeadRowProps: {
    className: 'claims-header-row',
    style: {
      background: mode === 'dark' ? theme.colors.dark[7] : theme.white,
      ...(!isAdmin ? claimTableStyles.header : {}),
    },
  },
});

const getTariffRowBackgroundColor = (
  mode: 'light' | 'dark',
  theme: MantineTheme
) =>
  mode === 'dark'
    ? alpha(theme.black, 0.32)
    : alpha(theme.colors.gray[2], 0.85);

const getTariffHierarchyRailColor = (
  mode: 'light' | 'dark',
  theme: MantineTheme
) => (mode === 'dark' ? theme.colors.gray[4] : theme.colors.gray[6]);

const getClaimRowVisualState = ({
  row,
  mode,
  theme,
  isSubmitted,
  isProvider,
  isAdmin,
}: {
  row: MRT_Row<ClaimTableRow>;
  mode: 'light' | 'dark';
  theme: MantineTheme;
  isSubmitted: boolean;
  isProvider: boolean;
  isAdmin: boolean;
}) => {
  const isTariffRow = isClaimTariffRow(row.original);
  const isSelected = row.getIsSelected();

  return {
    className: isTariffRow ? 'claims-tariff-row' : 'claims-parent-row',
    style: {
      cursor: row.getCanExpand() || !(isProvider || isAdmin) ? 'pointer' : '',
      opacity: isSubmitted ? 0.72 : 1,
      backgroundColor:
        isTariffRow && !isSelected
          ? getTariffRowBackgroundColor(mode, theme)
          : '',
      boxShadow: isTariffRow
        ? `inset 6px 0 0 ${getTariffHierarchyRailColor(mode, theme)}`
        : undefined,
    },
  };
};

const getClaimCellVisualState = ({
  cellId,
  row,
  mode,
  theme,
}: {
  cellId: string;
  row: MRT_Row<ClaimTableRow>;
  mode: 'light' | 'dark';
  theme: MantineTheme;
}) => {
  if (cellId === 'mrt-row-select') {
    return {
      onMouseDown: (event: MouseEvent) => event.stopPropagation(),
      onClick: (event: MouseEvent) => event.stopPropagation(),
      onDoubleClick: (event: MouseEvent) => event.stopPropagation(),
      style: {
        paddingLeft: 4,
        paddingRight: 4,
        cursor: 'pointer',
      },
    };
  }

  if (cellId === 'mrt-row-expand' && isClaimTariffRow(row.original)) {
    return {
      style: {
        borderInlineStart: `3px solid ${getTariffHierarchyRailColor(
          mode,
          theme
        )}`,
        paddingLeft: 4,
        paddingRight: 4,
      },
    };
  }

  return { style: {} };
};

const areSortingEqual = (
  left: MRT_SortingState = [],
  right: MRT_SortingState = []
) => JSON.stringify(left) === JSON.stringify(right);

const cloneSorting = (sorting: MRT_SortingState = []): MRT_SortingState =>
  sorting.map((sort) => ({ ...sort }));

const areGlobalFiltersEqual = (
  left: string | undefined,
  right: string | undefined
) => (left ?? '') === (right ?? '');
